import WarningAmber from "@mui/icons-material/WarningAmber";
import { Stack, Tooltip, Typography } from "@mui/material";
import { DisplayOnlyField } from "components/Forms/DisplayOnlyField";
import { Issue } from "generated-graphql/graphql";
import { ComponentProps } from "react";

type V2DisplayOnlyFieldProps = ComponentProps<typeof DisplayOnlyField> & {
  issues?: Issue[];
};

/**
 * DisplayOnlyField with 8px label spacing and wrapping values for the V2 facility profile.
 *
 * When `issues` are provided:
 * - Render as an orange warning icon with "Needs attention" text,
 *   with a tooltip listing the issues on hover
 *
 * When the field is empty, the issue indicators render in place of the empty value
 * placeholder so they sit flush against the label instead of leaving a ~16px gap.
 */
export function V2DisplayOnlyField({ issues, ...props }: V2DisplayOnlyFieldProps) {
  const hasIssues = issues && issues.length > 0;
  const isEmpty = !props.text && !props.renderValue;
  const showIssuesAsValue = isEmpty && hasIssues;

  const errorIndicator = hasIssues && (
    <Tooltip
      title={
        <Stack spacing={0.5}>
          {issues.map((issue, i) => (
            <Typography key={i} variant="body2" fontSize="12px">
              {issue.message}
            </Typography>
          ))}
        </Stack>
      }
      arrow
      placement="bottom-start"
    >
      <Stack
        direction="row"
        alignItems="center"
        spacing={0.5}
        sx={{
          cursor: "default",
          mt: showIssuesAsValue ? 0 : 0.5,
          width: "fit-content",
        }}
      >
        <WarningAmber sx={{ fontSize: "1rem", color: "error.main" }} />
        <Typography variant="body2" fontSize="0.75rem" color="error.main" fontStyle="italic">
          needs attention
        </Typography>
      </Stack>
    </Tooltip>
  );

  const issueBlock = <Stack spacing={0}>{errorIndicator}</Stack>;

  return (
    <Stack spacing={0} sx={{ mb: hasIssues ? 1 : 0 }}>
      <DisplayOnlyField
        labelSpacing={1}
        wrapValue
        {...props}
        renderValue={showIssuesAsValue ? () => issueBlock : props.renderValue}
      />

      {!showIssuesAsValue && errorIndicator}
    </Stack>
  );
}
